/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React, { useState } from 'react';
import { ITaskCreationFlowProps } from '../ModifyServices.Model';
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import theme from '../../../../../../Shared/Themes/theme';
import { buttonStyles } from '../../../../../Jobs/Components/UpdateShiftDate/UpdateShiftDate.Style';
import Button, { ButtonType } from '../../../../../../Shared/Components/Common/Buttons/Button';
import { JobNameContainer } from '../../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import CustomDropDownAutoComplete from '../../../../../../Shared/Components/Common/CustomDropDown/CustomDropdownAutoComplete';
import { Icons } from '../../../../../../Shared/Constants/Icons';
import { useTranslation } from 'react-i18next';
import { BoxShadow } from '../../../../../../Shared/Constants/Style.Constants';
import { Options } from '../../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import ModifyPopUp from '../../../../../Jobs/Components/JobDetails/ModifyPopUp';
import { CreateTaskSubtaskMappingDto } from '../../../../../../Shared/Models';
import { isEmpty } from 'lodash';
import { ServiceCreationType, SUBTASK_TYPE } from '../../../../../../Shared/Constants/App';
import { styles } from '../ModifyServices.Style';

const SelectExistingTaskForm: React.FC<ITaskCreationFlowProps> = (props) => {
  const {
    selectedTask,
    selectedSubTask,
    taskOptions,
    taskSubTasks,
    handleTaskChanges,
    handlePaginationChange,
    isTasksLoading,
    totalCount,
    currentPage,
    searchString,
    setSearchString,
    setAddNewTask,
    setNextClick,
    createSubTask,
    setSelectedSubTask,
    setSubTasksOpen,
    isSubTasksOpen,
    setCreateSubTask,
    selectedSubTaskType,
    handleSubTaskTypeChange,
    mainTasksObj,
    setServiceTaskMapping,
    handleCreateTask,
    handleServiceTaskMapping,
    isServiceTaskLoading,
    setIsEditService,
    handleTaskSubTaskMapping,
    setSelectedTask,
    setSelectedServiceId,
    setSelectedOption,
  } = props;

  const [isConfirmButtonLoading, setIsConfirmButtonLoading] = useState(false);
  const [isCreateSubTaskButtonLoading, setIsCreateSubTaskButtonLoading] = useState(false);
  const [isConfirmChanges, setIsConfirmChanges] = useState(false);
  const [subTaskForm, setSubTaskForm] = useState({ name: '', description: '' });
  const [selectedTaskType, setSelectedTaskType] = useState<string>('');

  const { radioButton, radioButtonText, textField, headingText } = buttonStyles;
  const { t } = useTranslation(['taskManagement']);

  const updateSubTaskForm = (field: 'name' | 'description', value: string) => {
    setSubTaskForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTaskTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTaskType((event.target as HTMLInputElement).value);
  };

  const resetForm = () => {
    setAddNewTask(false);
    setCreateSubTask(false);
    setIsEditService(false);
    setServiceTaskMapping(undefined);
    setSelectedTask('');
    setSearchString('');
    setNextClick(false);
    setSubTasksOpen(false);
    setSelectedSubTask('');
    setSubTaskForm({ name: '', description: '' });
    setSelectedServiceId(null);
    setSelectedOption('');
  };

  const createMainTaskData = () => ({
    mdTaskId: mainTasksObj?.mdTaskId,
    mdTaskName: mainTasksObj?.taskName,
    mdTaskDescription: mainTasksObj?.taskDescription,
    isProofOfCompletionRequired: false,
    isInspectionRequired: false,
    inspectionEntity: '',
    subTasks: [],
  });

  const createTaskSubTaskPayload = (subTaskData: any): CreateTaskSubtaskMappingDto => ({
    tasks: [
      {
        mdTaskId: mainTasksObj?.mdTaskId || '',
        mdTaskName: mainTasksObj?.taskName || '',
        mdTaskDescription: mainTasksObj?.taskDescription || '',
        subTasks: [
          {
            mdTaskId: subTaskData.mdTaskId || '',
            mdTaskName: subTaskData.mdTaskName,
            mdTaskDescription: subTaskData.mdTaskDescription,
          },
        ],
      },
    ],
  });

  const addTaskToMapping = (mainTaskData: any) => {
    setServiceTaskMapping((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        type: selectedTaskType,
        tasks: [...(prev.tasks ?? []), mainTaskData],
      };
    });
  };

  const handleConfirmButton = () => {
    try {
      setIsConfirmButtonLoading(true);

      if (!createSubTask) {
        const mainTaskData = createMainTaskData();

        if (!isEmpty(selectedSubTask)) {
          const selectedSubTaskObj = taskSubTasks.find((item) => item.mdTaskId === selectedSubTask);

          const subTaskData = {
            mdTaskId: selectedSubTaskObj.mdTaskId ?? '',
            mdTaskName: selectedSubTaskObj.mdTaskName ?? '',
            mdTaskDescription: selectedSubTaskObj.taskDescription ?? '',
          };

          (mainTaskData.subTasks as any[]).push(subTaskData as any);

          const payload = createTaskSubTaskPayload(subTaskData);
          handleTaskSubTaskMapping(payload);
        }

        addTaskToMapping(mainTaskData);
        setIsConfirmChanges(true);
      }
    } catch (e) {
      console.error('Error occurred while confirming task:', e);
    } finally {
      setIsConfirmButtonLoading(false);
    }
  };

  const handleCreateSubTask = async () => {
    try {
      setIsCreateSubTaskButtonLoading(true);

      if (createSubTask) {
        const mainTaskData = createMainTaskData();

        const response = await handleCreateTask(subTaskForm.name, subTaskForm.description);
        if (!response) return;

        const subTaskData = {
          mdTaskId: response.mdTaskId,
          mdTaskName: subTaskForm.name,
          mdTaskDescription: subTaskForm.description,
          creationType: selectedSubTaskType,
        };

        (mainTaskData.subTasks as any[]).push(subTaskData as any);

        addTaskToMapping(mainTaskData);

        if (selectedSubTaskType === SUBTASK_TYPE.PERMANENT) {
          const payload = createTaskSubTaskPayload(subTaskData);
          handleTaskSubTaskMapping(payload);
        }

        setIsConfirmChanges(true);
      }
    } catch (e) {
      console.error('Error occurred while creating subtask:', e);
    } finally {
      setIsCreateSubTaskButtonLoading(false);
    }
  };

  const renderRadioOptions = () => (
    <FormControl component="fieldset">
      <Typography
        sx={{
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.h3?.fontSize,
          fontWeight: theme.typography.fontWeightBold,
        }}>
        <span>
          {t('taskManagement:DoYouWantToAddSubTaskPermanentlyOrSchedule')}
          <span className="mandatory-field"> *</span>
        </span>
      </Typography>
      <RadioGroup value={selectedSubTaskType} onChange={handleSubTaskTypeChange}>
        <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
          <FormControlLabel
            value={SUBTASK_TYPE.PERMANENT}
            control={<Radio sx={radioButton} />}
            label={t('taskManagement:Permanently')}
            sx={radioButtonText}
          />
        </Box>
        <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
          <FormControlLabel
            value={SUBTASK_TYPE.SCHEDULE}
            control={<Radio sx={radioButton} />}
            label={t('taskManagement:Schedule')}
            sx={radioButtonText}
          />
        </Box>
      </RadioGroup>
    </FormControl>
  );

  const renderTaskRadioOptions = () => (
    <FormControl component="fieldset">
      <Typography
        sx={{
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.h3?.fontSize,
          fontWeight: theme.typography.fontWeightBold,
          marginTop: '2rem',
        }}>
        <span>
          {t('taskManagement:DoYouWantToAddTaskPermanentlyOrSchedule')}
          <span className="mandatory-field"> *</span>
        </span>
      </Typography>
      <RadioGroup value={selectedTaskType} onChange={handleTaskTypeChange}>
        <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
          <FormControlLabel
            value={ServiceCreationType.SCHEDULE_LEVEL}
            control={<Radio sx={radioButton} />}
            label={t('taskManagement:ScheduleLevel')}
            sx={radioButtonText}
          />
        </Box>
        <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
          <FormControlLabel
            value={ServiceCreationType.CUSTOMER_LEVEL}
            control={<Radio sx={radioButton} />}
            label={t('taskManagement:CustomerLevel')}
            sx={radioButtonText}
          />
        </Box>
      </RadioGroup>
    </FormControl>
  );

  const renderCreateSubTaskForm = () => (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <JobNameContainer>
          <TextField
            sx={textField}
            id="subtask-name"
            label={
              <span>
                {t('taskManagement:SubTaskName')}
                <span className="mandatory-field"> *</span>
              </span>
            }
            value={subTaskForm.name}
            onChange={(e) => updateSubTaskForm('name', e.target.value)}
          />
        </JobNameContainer>
        <JobNameContainer>
          <TextField
            sx={textField}
            id="subtask-description"
            label={
              <span>
                {t('taskManagement:SubTaskDescription')}
                <span className="mandatory-field"> *</span>
              </span>
            }
            value={subTaskForm.description}
            onChange={(e) => updateSubTaskForm('description', e.target.value)}
          />
        </JobNameContainer>
      </Box>
      {renderRadioOptions()}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
        <Button
          type={ButtonType.Secondary}
          label={t('taskManagement:Cancel')}
          onClick={() => setCreateSubTask(false)}
        />
        <Button
          type={ButtonType.Primary}
          label={t('taskManagement:CreateSubTaskButton')}
          onClick={handleCreateSubTask}
          disabled={!subTaskForm.name || !subTaskForm.description || !selectedSubTaskType}
          isLoading={isCreateSubTaskButtonLoading}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography sx={headingText}>{t('taskManagement:Tasks')}</Typography>
        <CustomDropDownAutoComplete
          title={t('taskManagement:Tasks')}
          options={taskOptions}
          selectedValue={selectedTask}
          onChange={handleTaskChanges}
          isLoading={isTasksLoading}
          totalCounts={totalCount}
          handlePaginationChanges={handlePaginationChange}
          currentPages={currentPage}
          searchString={searchString}
          setSearchString={setSearchString}
          dropDownHeight={500}
          isInline={true}
          isDisabled={createSubTask}
        />
        {renderTaskRadioOptions()}
        <Typography sx={{ ...headingText, marginTop: '2rem' }}>{t('taskManagement:SubTasks')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', marginBottom: '2.5rem' }}>
          <TextField
            select
            sx={{
              ...textField,
              opacity: createSubTask ? '0.5' : '1',
              cursor: createSubTask ? 'not-allowed' : 'pointer',
            }}
            disabled={createSubTask}
            label={<span>{t('taskManagement:SubTasks')}</span>}
            value={selectedSubTask}
            onChange={(event) => setSelectedSubTask(event?.target?.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSubTasksOpen(!isSubTasksOpen)}
                    style={{
                      padding: 0,
                      opacity: createSubTask ? '0.5' : '1',
                      cursor: createSubTask ? 'not-allowed' : 'pointer',
                    }}
                    disabled={createSubTask}>
                    <img
                      src={isSubTasksOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                      alt={isSubTasksOpen ? 'down' : 'up'}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            SelectProps={{
              open: isSubTasksOpen,
              IconComponent: () => null,
              onOpen: () => setSubTasksOpen(true),
              onClose: () => setSubTasksOpen(false),
              MenuProps: {
                PaperProps: {
                  style: {
                    boxShadow: BoxShadow.BoxShadow,
                  },
                },
              },
            }}>
            {taskSubTasks && taskSubTasks?.length > 0 ? (
              taskSubTasks?.map((schedule: any, index: number) => (
                <Options key={index} value={schedule?.mdTaskId}>
                  {schedule?.taskName}
                </Options>
              ))
            ) : (
              <Options disabled>{t('taskManagement:NoTasksFoundInDropdown')}</Options>
            )}
          </TextField>
          <Box
            sx={{
              ...styles.addNewLink,
              opacity: createSubTask || !selectedTask || !selectedTaskType ? '0.5' : '1',
              cursor: createSubTask || !selectedTask || !selectedTaskType ? 'not-allowed' : 'pointer',
            }}
            onClick={() => {
              if (createSubTask || !selectedTask || !selectedTaskType) return;
              setCreateSubTask(true);
            }}>
            <img src={Icons.AddIcon} width={24} height={24} alt={t('altTexts:add')} />
            {t('taskManagement:AddNewSubTask')}
          </Box>
        </Box>
        {createSubTask && renderCreateSubTaskForm()}
        {!createSubTask && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
            <Button
              type={ButtonType.Secondary}
              label={t('taskManagement:Cancel')}
              onClick={() => {
                setAddNewTask(false);
                setNextClick(false);
              }}
            />
            <Button
              type={ButtonType.Primary}
              label={t('taskManagement:Confirm')}
              onClick={handleConfirmButton}
              disabled={!selectedTask || !selectedTaskType}
              isLoading={isConfirmButtonLoading}
            />
          </Box>
        )}
      </Box>
      <ModifyPopUp
        open={isConfirmChanges}
        handleClose={() => setIsConfirmChanges(false)}
        handleOnCloseJob={() => {
          handleServiceTaskMapping();
          setIsConfirmChanges(false);
          resetForm();
        }}
        loading={isServiceTaskLoading}
        heading={t('taskManagement:ConfirmChanges')}
        text={t('taskManagement:AreYouSureYouWantToConfirmThisChanges')}
      />
    </>
  );
};

export default SelectExistingTaskForm;
